import { nanoid } from 'nanoid/non-secure';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
    SimulationEdge,
    SimulationGraph,
    SimulationNode,
    SimulationNodeData,
    SimulationResultPayload,
    SimulationRunConfig
} from '../models/simulation';
import { extractVariableNames, buildNameToIdMap, resolveVariableNamesToIds } from '../utils/expressionParser';
import { layoutGraphWithDefault } from '../utils/layout';
import { historyMiddleware, type HistoryState } from './historyMiddleware';
import type { ValidationError } from '../features/simulation/validation';
import { DEFAULT_NODE_POSITION, DEFAULT_TRIAL_COUNT, HISTORY_LIMIT } from '../config/constants';
import { initializeCustomUnits, createCustomUnit, normalizeUnit } from '../utils/units';

/**
 * Normalizes all units in a graph by removing spaces around operators.
 * This ensures consistent unit formatting across the entire graph.
 */
function normalizeGraphUnits(graph: SimulationGraph): SimulationGraph {
    const normalizedGraph = structuredClone(graph);

    // Normalize units in distribution nodes
    normalizedGraph.nodes.forEach((node) => {
        if (node.data.type === 'distribution' && node.data.unit) {
            node.data.unit = normalizeUnit(node.data.unit);
        }
    });

    return normalizedGraph;
}

/**
 * Generate a unique name by appending (2), (3), etc. if name exists
 */
const makeUniqueNameInternal = (proposedName: string, existingNames: Set<string>): string => {
    if (!existingNames.has(proposedName)) {
        return proposedName;
    }

    let counter = 2;
    while (existingNames.has(`${proposedName} (${counter})`)) {
        counter++;
    }
    return `${proposedName} (${counter})`;
};

/**
 * Regenerate edges for a node based on its dependencies
 */
const regenerateEdgesForNode = (
    nodeId: string,
    node: SimulationNode,
    allNodes: SimulationNode[],
    edges: SimulationEdge[]
): SimulationEdge[] => {
    // Remove existing edges targeting this node
    const filteredEdges = edges.filter(edge => edge.target !== nodeId);

    const nameToIdMap = buildNameToIdMap(allNodes);
    let sourceIds: string[] = [];

    if (node.data.type === 'formula') {
        // Extract variable names from expression (guard against undefined)
        if (node.data.expression && typeof node.data.expression === 'string') {
            const variableNames = extractVariableNames(node.data.expression);
            sourceIds = resolveVariableNamesToIds(variableNames, nameToIdMap);
        }
    }

    // Create new edges
    const newEdges = sourceIds.map(sourceId => ({
        id: nanoid(),
        source: sourceId,
        target: nodeId
    }));

    return [...filteredEdges, ...newEdges];
};

export interface SimulationState extends HistoryState {
    graph: SimulationGraph;
    selectedNodeId?: string;
    runConfig: SimulationRunConfig;
    lastResult?: SimulationResultPayload;
    runErrors: ValidationError[];
    isRunning: boolean;
    runProgress?: { completedTrials: number; totalTrials: number };
    currentSimulationId?: string;
    currentSimulationName?: string;
    currentVersionId?: string;
    currentVersionName?: string;
    currentAccessLevel?: 'owner' | 'editor' | 'viewer';
    autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
    lastAutoSaveAt?: string;
    autoSaveError?: string;
    activeInputsCount: number;
    setWorkspaceContext: (simulationId?: string, simulationName?: string, versionId?: string, versionName?: string, accessLevel?: 'owner' | 'editor' | 'viewer') => void;
    clearWorkspaceContext: () => void;
    addNode: (data: SimulationNodeData, position?: { x: number; y: number }) => SimulationNode;
    updateNode: (nodeId: string, updater: (node: SimulationNode) => void) => void;
    removeNode: (nodeId: string) => void;
    addEdge: (edge: Omit<SimulationEdge, 'id'>) => SimulationEdge;
    removeEdge: (edgeId: string) => void;
    setSelection: (nodeId?: string) => void;
    setRunConfig: (config: Partial<SimulationRunConfig>) => void;
    setIsRunning: (value: boolean) => void;
    setResult: (result?: SimulationResultPayload) => void;
    setPartialResult: (partialResults: Record<string, any>, completedTrials: number, totalTrials: number) => void;
    setRunProgress: (completedTrials: number, totalTrials: number) => void;
    setRunErrors: (errors: ValidationError[]) => void;
    clearRunErrors: () => void;
    loadGraph: (graph: SimulationGraph) => void;
    setNodePositions: (positions: Record<string, { x: number; y: number }>) => void;
    regenerateEdges: (nodeId: string) => void;
    tidyLayout: () => Promise<void>;
    setAutoSaveStatus: (status: 'idle' | 'saving' | 'saved' | 'error', errorMessage?: string) => void;
    setLastAutoSaveAt: (timestamp?: string) => void;
    addCustomUnit: (unitName: string) => { success: boolean; error?: string };
    registerActiveInput: (inputId: string) => void;
    unregisterActiveInput: (inputId: string) => void;
    hasActiveInputs: () => boolean;
}

// Helper to create an empty graph
const createEmptyGraph = (): SimulationGraph => {
    const now = new Date().toISOString();
    return {
        id: nanoid(),
        name: 'Untitled Simulation',
        version: 1,
        nodes: [],
        edges: [],
        createdAt: now,
        updatedAt: now,
        dataUpdatedAt: now,
        customUnits: []
    };
};

export const useSimulationStore = create<SimulationState>()(
    historyMiddleware(
        immer((set, get) => {
            const initialGraph = createEmptyGraph();
            const activeInputs = new Set<string>();

            return {
                // History state - these will be overridden by historyMiddleware
                past: [],
                future: [],
                canUndo: false,
                canRedo: false,
                undo: () => {
                    // Implemented by historyMiddleware
                },
                redo: () => {
                    // Implemented by historyMiddleware
                },
                clearHistory: () => {
                    // Implemented by historyMiddleware
                },
                graph: initialGraph,
                runConfig: { trials: DEFAULT_TRIAL_COUNT, collectSamples: true },
                runErrors: [],
                isRunning: false,
                currentAccessLevel: undefined,
                autoSaveStatus: 'idle',
                lastAutoSaveAt: undefined,
                autoSaveError: undefined,
                activeInputsCount: 0,
                tidyLayout: async () => {
                    const graphSnapshot = get().graph;
                    try {
                        const positions = await layoutGraphWithDefault(graphSnapshot);
                        set((state) => {
                            if (state.graph.id !== graphSnapshot.id) {
                                return;
                            }
                            state.graph.nodes.forEach((node) => {
                                const pos = positions[node.id];
                                if (pos) {
                                    node.position = { x: pos.x, y: pos.y };
                                }
                            });
                            // Position-only updates shouldn't affect dataUpdatedAt
                            state.graph.updatedAt = new Date().toISOString();
                        });
                    } catch (err) {
                        // Error already logged by layout worker caller when needed.
                    }
                },
                addNode: (data, position = DEFAULT_NODE_POSITION) => {
                    // Prevent adding nodes if user is viewer
                    const state = get();
                    if (state.currentAccessLevel === 'viewer') {
                        throw new Error('Viewers cannot add new nodes');
                    }

                    let newNode: SimulationNode | null = null;

                    set((state) => {
                        // Ensure unique name
                        const existingNames = new Set<string>(state.graph.nodes.map(n => n.data.name));
                        const uniqueName = makeUniqueNameInternal(data.name, existingNames);

                        const nodeData = { ...data, name: uniqueName };
                        // Normalize unit if it's a distribution node
                        if (nodeData.type === 'distribution' && nodeData.unit) {
                            nodeData.unit = normalizeUnit(nodeData.unit);
                        }
                        newNode = {
                            id: nanoid(),
                            data: nodeData,
                            position
                        };

                        state.graph.nodes.push(newNode);

                        // Auto-generate edges if it's a formula node
                        if (nodeData.type === 'formula') {
                            state.graph.edges = regenerateEdgesForNode(
                                newNode.id,
                                newNode,
                                state.graph.nodes,
                                state.graph.edges
                            );
                        }

                        const now = new Date().toISOString();
                        state.graph.updatedAt = now;
                        state.graph.dataUpdatedAt = now; // Adding a node is a data change
                    });

                    return newNode!;
                },
                updateNode: (nodeId, updater) => {
                    set((state) => {
                        const target = state.graph.nodes.find((node) => node.id === nodeId);
                        if (!target) return;

                        const oldName = target.data.name;
                        const oldDescription = target.data.description;
                        const oldExpression = target.data.type === 'formula' ? target.data.expression : null;
                        const oldConfig = target.data.type === 'distribution' ? JSON.stringify(target.data.config) : null;
                        const oldIsOutput = target.data.isOutput;
                        const oldStatistic = target.data.statistic;
                        const oldTags = target.data.tags ? JSON.stringify([...target.data.tags].sort()) : null;

                        // Apply the update
                        updater(target);

                        // Normalize unit if it's a distribution node
                        if (target.data.type === 'distribution' && target.data.unit) {
                            target.data.unit = normalizeUnit(target.data.unit);
                        }

                        const newName = target.data.name;
                        const newDescription = target.data.description;
                        const newExpression = target.data.type === 'formula' ? target.data.expression : null;
                        const newConfig = target.data.type === 'distribution' ? JSON.stringify(target.data.config) : null;
                        const newIsOutput = target.data.isOutput;
                        const newStatistic = target.data.statistic;
                        const newTags = target.data.tags ? JSON.stringify([...target.data.tags].sort()) : null;

                        let dataChanged = false;

                        // Check if name changed
                        if (newName !== oldName) {
                            // Ensure the new name is unique
                            const existingNames = new Set<string>(
                                state.graph.nodes
                                    .filter(n => n.id !== nodeId)
                                    .map(n => n.data.name)
                            );
                            const uniqueName = makeUniqueNameInternal(newName, existingNames);
                            target.data.name = uniqueName;
                            dataChanged = true;

                            // Update all formulas that reference the old name
                            let formulasUpdated = false;
                            state.graph.nodes.forEach(node => {
                                if (node.data.type === 'formula') {
                                    const oldFormulaExpression = node.data.expression;
                                    const pattern = new RegExp(
                                        `\\$\\{["']${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']\\}`,
                                        'g'
                                    );
                                    node.data.expression = node.data.expression.replace(
                                        pattern,
                                        `\${"${uniqueName}"}`
                                    );

                                    // If formula changed, mark data as changed
                                    if (node.data.expression !== oldFormulaExpression) {
                                        formulasUpdated = true;
                                    }

                                    // Regenerate edges for this formula node
                                    state.graph.edges = regenerateEdgesForNode(
                                        node.id,
                                        node,
                                        state.graph.nodes,
                                        state.graph.edges
                                    );
                                }
                            });

                            // Name changes trigger data changes if they affected other formulas
                            if (formulasUpdated) {
                                dataChanged = true;
                            }
                        }

                        // Check if expression or config changed
                        const expressionChanged = newExpression !== null && newExpression !== oldExpression;
                        const configChanged = newConfig !== null && newConfig !== oldConfig;
                        const isOutputChanged = newIsOutput !== oldIsOutput;
                        const statisticChanged = newStatistic !== oldStatistic;
                        const tagsChanged = newTags !== oldTags;

                        if (expressionChanged) {
                            // Regenerate edges for this node
                            state.graph.edges = regenerateEdgesForNode(
                                nodeId,
                                target,
                                state.graph.nodes,
                                state.graph.edges
                            );
                            dataChanged = true;
                        }

                        if (configChanged || isOutputChanged || statisticChanged || tagsChanged) {
                            dataChanged = true;
                        }

                        // Always update general timestamp
                        state.graph.updatedAt = new Date().toISOString();

                        // Only update data timestamp if simulation-relevant data changed
                        if (dataChanged) {
                            state.graph.dataUpdatedAt = new Date().toISOString();
                        }
                    });
                },
                removeNode: (nodeId) => {
                    set((state) => {
                        state.graph.nodes = state.graph.nodes.filter((node) => node.id !== nodeId);
                        state.graph.edges = state.graph.edges.filter(
                            (edge) => edge.source !== nodeId && edge.target !== nodeId
                        );
                        if (state.selectedNodeId === nodeId) {
                            state.selectedNodeId = undefined;
                        }
                        // Remove any stale results associated with the deleted node
                        if (state.lastResult && state.lastResult.results) {
                            // Delete the exact node's result
                            delete state.lastResult.results[nodeId];

                            // Also ensure there are no results for nodes that no longer exist
                            const existingIds = new Set(state.graph.nodes.map(n => n.id));
                            Object.keys(state.lastResult.results).forEach((resultNodeId) => {
                                if (!existingIds.has(resultNodeId)) {
                                    delete state.lastResult!.results[resultNodeId];
                                }
                            });
                        }
                        const now = new Date().toISOString();
                        state.graph.updatedAt = now;
                        state.graph.dataUpdatedAt = now; // Removing a node is a data change
                    });
                },
                addEdge: (edge) => {
                    const newEdge: SimulationEdge = { id: nanoid(), ...edge };
                    set((state) => {
                        state.graph.edges.push(newEdge);
                        state.graph.updatedAt = new Date().toISOString();
                    });
                    return newEdge;
                },
                removeEdge: (edgeId) => {
                    set((state) => {
                        state.graph.edges = state.graph.edges.filter((edge) => edge.id !== edgeId);
                        state.graph.updatedAt = new Date().toISOString();
                    });
                },
                setSelection: (nodeId) => {
                    set((state) => {
                        state.selectedNodeId = nodeId;
                    });
                },
                setRunConfig: (config) => {
                    set((state) => {
                        state.runConfig = { ...state.runConfig, ...config, collectSamples: true };
                    });
                },
                setIsRunning: (value) => {
                    set((state) => {
                        state.isRunning = value;
                    });
                },
                setResult: (result) => {
                    set((state) => {
                        state.lastResult = result;
                        state.isRunning = false;
                        state.runProgress = undefined;
                        if (result) {
                            state.runErrors = [];
                        }
                    });
                },
                setPartialResult: (partialResults, completedTrials, totalTrials) => {
                    set((state) => {
                        // Initialize or update lastResult with partial results
                        if (!state.lastResult) {
                            state.lastResult = {
                                graphId: state.graph.id,
                                results: partialResults,
                                runConfig: state.runConfig,
                                durationMs: 0
                            };
                        } else {
                            // Merge partial results into existing results
                            Object.assign(state.lastResult.results, partialResults);
                        }
                        // Update progress tracking
                        state.runProgress = { completedTrials, totalTrials };
                    });
                },
                setRunProgress: (completedTrials, totalTrials) => {
                    set((state) => {
                        state.runProgress = { completedTrials, totalTrials };
                    });
                },
                setRunErrors: (errors) => {
                    set((state) => {
                        state.runErrors = errors;
                        if (errors.length > 0) {
                            state.lastResult = undefined;
                        }
                    });
                },
                clearRunErrors: () => {
                    set((state) => {
                        state.runErrors = [];
                    });
                },
                setNodePositions: (positions) => {
                    set((state) => {
                        state.graph.nodes.forEach((node) => {
                            const next = positions[node.id];
                            if (next) {
                                node.position = { x: next.x, y: next.y };
                            }
                        });
                        // Position changes don't affect simulation data
                        state.graph.updatedAt = new Date().toISOString();
                        // Intentionally NOT updating dataUpdatedAt
                    });
                },
                loadGraph: (graph) => {
                    set((state) => {
                        // Normalize units in the graph before loading
                        const normalizedGraph = normalizeGraphUnits(graph);
                        state.graph = normalizedGraph;
                        state.selectedNodeId = undefined;
                        state.lastResult = undefined;

                        // Ensure customUnits array exists (backward compatibility)
                        if (!state.graph.customUnits) {
                            state.graph.customUnits = [];
                        }

                        // Initialize custom units in math.js
                        initializeCustomUnits(state.graph.customUnits);

                        // Regenerate all edges based on formula dependencies
                        let newEdges: SimulationEdge[] = [];
                        state.graph.nodes.forEach(node => {
                            if (node.data.type === 'formula') {
                                newEdges = regenerateEdgesForNode(
                                    node.id,
                                    node,
                                    state.graph.nodes,
                                    newEdges
                                );
                            }
                        });
                        state.graph.edges = newEdges;

                        const now = new Date().toISOString();
                        state.graph.updatedAt = now;

                        // Initialize dataUpdatedAt if not present
                        if (!state.graph.dataUpdatedAt) {
                            state.graph.dataUpdatedAt = now;
                        }
                    });
                },
                regenerateEdges: (nodeId) => {
                    set((state) => {
                        const node = state.graph.nodes.find(n => n.id === nodeId);
                        if (node) {
                            state.graph.edges = regenerateEdgesForNode(
                                nodeId,
                                node,
                                state.graph.nodes,
                                state.graph.edges
                            );
                            state.graph.updatedAt = new Date().toISOString();
                        }
                    });
                },
                setAutoSaveStatus: (status, errorMessage) => {
                    set((state) => {
                        state.autoSaveStatus = status;
                        state.autoSaveError = status === 'error' ? errorMessage : undefined;
                    });
                },
                setLastAutoSaveAt: (timestamp) => {
                    set((state) => {
                        state.lastAutoSaveAt = timestamp;
                    });
                },
                setWorkspaceContext: (simulationId, simulationName, versionId, versionName, accessLevel) => {
                    set((state) => {
                        state.currentSimulationId = simulationId;
                        state.currentSimulationName = simulationName;
                        state.currentVersionId = versionId;
                        state.currentVersionName = versionName;
                        state.currentAccessLevel = accessLevel;
                    });
                },
                clearWorkspaceContext: () => {
                    set((state) => {
                        state.currentSimulationId = undefined;
                        state.currentSimulationName = undefined;
                        state.currentVersionId = undefined;
                        state.currentVersionName = undefined;
                        state.currentAccessLevel = undefined;
                    });
                },
                addCustomUnit: (unitName) => {
                    const state = get();
                    const currentCustomUnits = state.graph.customUnits || [];

                    const result = createCustomUnit(unitName, currentCustomUnits);

                    if (result.success && result.updatedUnits.length > currentCustomUnits.length) {
                        // Only update state if a new unit was actually added
                        set((state) => {
                            state.graph.customUnits = result.updatedUnits;

                            // Update dataUpdatedAt since custom units affect simulation behavior
                            const now = new Date().toISOString();
                            state.graph.updatedAt = now;
                            state.graph.dataUpdatedAt = now;
                        });
                    }

                    return { success: result.success, error: result.error };
                },
                registerActiveInput: (inputId) => {
                    if (!activeInputs.has(inputId)) {
                        activeInputs.add(inputId);
                        set((state) => {
                            state.activeInputsCount = activeInputs.size;
                        });
                    }
                },
                unregisterActiveInput: (inputId) => {
                    if (activeInputs.has(inputId)) {
                        activeInputs.delete(inputId);
                        set((state) => {
                            state.activeInputsCount = activeInputs.size;
                        });
                    }
                },
                hasActiveInputs: () => {
                    return activeInputs.size > 0;
                }
            };
        }),
        {
            limit: HISTORY_LIMIT,
            exclude: ['isRunning', 'lastResult', 'runErrors', 'selectedNodeId', 'currentSimulationId', 'currentSimulationName', 'currentVersionId', 'currentVersionName'],
            equality: (a: any, b: any) => {
                // Only track changes to graph data, not UI state
                return a.graph?.dataUpdatedAt === b.graph?.dataUpdatedAt;
            }
        }
    )
);

export const useGraph = () => useSimulationStore((state) => state.graph);
export const useSelectedNodeId = () => useSimulationStore((state) => state.selectedNodeId);
export const useRunConfig = () => useSimulationStore((state) => state.runConfig);
export const useLastResult = () => useSimulationStore((state) => state.lastResult);
export const useNodeById = (nodeId: string) =>
    useSimulationStore((state) => state.graph.nodes.find((node) => node.id === nodeId));
export const useNodeResult = (nodeId: string) =>
    useSimulationStore((state) => state.lastResult?.results[nodeId]);
export const useRunErrors = () => useSimulationStore((state) => state.runErrors);
export const useIsRunning = () => useSimulationStore((state) => state.isRunning);
